import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  ChartBar,
  ChartLine,
  Tray,
  BookOpen,
  TestTube,
  GearSix,
  Bell,
  List as ListIcon,
  X,
  SignOut,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { fetchStats, fetchNotifications, markAllNotificationsRead } from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/", label: "Übersicht", icon: ChartBar, end: true, testid: "nav-overview" },
  { to: "/inbox", label: "Inbox", icon: Tray, testid: "nav-inbox" },
  { to: "/knowledge", label: "Wissen", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/simulator", label: "Simulator", icon: TestTube, testid: "nav-simulator" },
  { to: "/quality", label: "Qualität", icon: ChartLine, testid: "nav-quality" },
  { to: "/settings", label: "Einstellungen", icon: GearSix, testid: "nav-settings" },
];

export default function DashboardLayout() {
  const location = useLocation();
  const { user, signOut, configured: authConfigured } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const unread = notifs.filter((n) => !n.read).length;
  const currentNav = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || NAV[0];

  const refresh = async () => {
    try {
      const [s, n] = await Promise.all([fetchStats(), fetchNotifications()]);
      setStats(s);
      setNotifs(n);
    } catch {}
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [location.pathname]);

  useEffect(() => {
    setNavOpen(false); // schließt Mobile-Nav bei Routenwechsel
  }, [location.pathname]);

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    refresh();
  };

  const Sidebar = (
    <aside
      className="h-full w-64 border-r border-border-soft flex flex-col bg-white"
      data-testid="sidebar"
    >
      <div className="h-20 flex items-center gap-3 px-5 border-b border-border-soft">
        <div className="w-11 h-11 rounded-2xl bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
          <img
            src="/lulu-bot-logo.png"
            alt=""
            className="h-10 w-10 object-contain"
          />
        </div>
        <div className="leading-tight">
          <div className="text-xl font-bold text-primary tracking-tight">Lulu Bot</div>
          <div className="text-[11px] text-muted-foreground font-medium">AI Assistant</div>
        </div>
      </div>
      <nav className="flex-1 py-4 px-3">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 my-1 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-secondary text-primary"
                    : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground"
                }`
              }
            >
              <Icon size={20} weight="duotone" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      {stats && (
        <div className="mx-3 mb-3 p-4 rounded-xl bg-secondary/40 border border-border-soft flex gap-4 text-xs">
          <div className="flex-1">
            <div className="text-muted-foreground text-[11px]">Auto-Quote</div>
            <div className="font-display font-semibold text-2xl text-primary">
              {stats.auto_reply_rate}%
            </div>
          </div>
          <div className="flex-1">
            <div className="text-muted-foreground text-[11px]">Eskaliert</div>
            <div
              className={`font-display font-semibold text-2xl ${
                stats.escalated > 0 ? "text-destructive" : "text-foreground/40"
              }`}
            >
              {stats.escalated}
            </div>
          </div>
        </div>
      )}
      {authConfigured && user && (
        <div className="border-t border-border-soft p-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-secondary text-primary flex items-center justify-center text-xs font-semibold uppercase">
            {(user.email || "?").slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{user.email}</div>
            <div className="text-[10px] text-muted-foreground">eingeloggt</div>
          </div>
          <button
            onClick={signOut}
            className="p-1.5 hover:bg-secondary rounded-lg text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Logout"
            title="Logout"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      )}
    </aside>
  );

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Sidebar Desktop (>= md) — sticky/fix, scrollt nicht mit Page-Content */}
      <div className="hidden md:block sticky top-0 h-screen">{Sidebar}</div>

      {/* Sidebar Mobile (Drawer) */}
      {navOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setNavOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 md:hidden">{Sidebar}</div>
        </>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 md:h-20 border-b border-border-soft bg-white flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 -ml-2 hover:bg-secondary rounded-lg transition-colors"
              onClick={() => setNavOpen(true)}
              aria-label="Menü"
            >
              <ListIcon size={22} weight="bold" />
            </button>
            <div className="font-display font-semibold text-lg md:text-xl">
              {currentNav.label}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="relative p-2 hover:bg-secondary rounded-xl transition-colors"
                  data-testid="notifications-trigger"
                  aria-label="Benachrichtigungen"
                >
                  <Bell size={20} weight="duotone" />
                  {unread > 0 && (
                    <span
                      className="absolute top-1 right-1 bg-destructive text-white text-[10px] font-semibold h-4 min-w-4 px-1 rounded-full flex items-center justify-center shadow-sm"
                      data-testid="notif-badge"
                    >
                      {unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-80 sm:w-96 p-0 border-border"
              >
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="font-display font-semibold text-base">
                    Benachrichtigungen
                  </div>
                  {unread > 0 && (
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={handleMarkAllRead}
                      data-testid="notif-mark-read-all"
                    >
                      Alle als gelesen
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-auto">
                  {notifs.length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                      Noch keine Benachrichtigungen.
                    </div>
                  )}
                  {notifs.map((n) => (
                    <div
                      key={n.id}
                      className={`p-4 border-b border-border last:border-b-0 ${
                        n.read ? "" : "bg-secondary/40"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-destructive" />
                        <div className="font-medium text-sm">{n.title}</div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2 ml-4">
                        {n.preview}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {authConfigured && user && (
              <div className="hidden md:flex items-center gap-2 pl-2">
                <div className="w-8 h-8 rounded-full bg-secondary text-primary flex items-center justify-center text-xs font-semibold uppercase">
                  {(user.email || "?").slice(0, 2)}
                </div>
                <div className="text-sm font-medium text-foreground/80">Admin</div>
              </div>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto min-h-0">
          <Outlet context={{ stats, refresh }} />
        </div>
      </main>
    </div>
  );
}
